// ProcessingOrb.jsx
//
// Universal "user is waiting" loading visual. Used by the Agenda
// generation phase, the Intel Brief generation phase, the brief narration
// (TTS) loading phase, the Goal voice transcription phase, and the Goal
// save (LLM-parse) phase.
//
// The look:
//   ▸ A small, breathing core dot in the active accent color, scaling up
//     and down on a slow 1.8s loop with a glow that breathes alongside it.
//   ▸ Three sonar rings expanding outward from the core, each fading as
//     it grows, staggered 0.6s apart so the rhythm reads as a heartbeat.
//   ▸ A faint rotating halo behind the core for subtle continuous motion
//     (so it never looks frozen even between sonar pulses).
//
// All animation is pure CSS — keyframes live in index.css under
// `orbSonar` / `orbCore` / `orbHalo`. No requestAnimationFrame, so this
// is cheap on iOS WKWebView and stays smooth even when the JS thread is
// busy hitting an LLM endpoint.

export function ProcessingOrb({
  size = 64,
  label,
  sublabel,
  // Optional: a string used as the React key on the `label` so changing
  // copy mid-loop fades the text instead of dumping it instantly.
  labelKey,
}) {
  const accentRgb = 'var(--accent-rgb, 45, 156, 255)';

  const core = {
    position: 'absolute', top: '50%', left: '50%',
    width: size * 0.32, height: size * 0.32,
    marginTop: -(size * 0.16), marginLeft: -(size * 0.16),
    borderRadius: '50%',
    background: 'var(--grad-cta)',
    animation: 'orbCore 1.8s ease-in-out infinite',
    zIndex: 3,
  };

  const halo = {
    position: 'absolute', inset: 0,
    borderRadius: '50%',
    background: `conic-gradient(from 0deg, transparent 0deg, rgba(${accentRgb}, 0.18) 90deg, transparent 180deg)`,
    animation: 'orbHalo 4.2s linear infinite',
    zIndex: 1,
    opacity: 0.7,
  };

  const ring = (delay) => ({
    position: 'absolute', inset: 0,
    borderRadius: '50%',
    border: `2px solid rgba(${accentRgb}, 0.55)`,
    animation: `orbSonar 1.8s cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}s infinite`,
    zIndex: 2,
    transformOrigin: '50% 50%',
  });

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        position: 'relative',
        width: size, height: size,
      }}>
        <div style={halo}/>
        <div style={ring(0)}/>
        <div style={ring(0.6)}/>
        <div style={ring(1.2)}/>
        <div style={core}/>
      </div>
      {label && (
        <div
          key={labelKey || label}
          style={{
            fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600,
            color: 'var(--fg-1)', letterSpacing: '-0.005em',
            animation: 'pulseOpacity 1.8s ease-in-out infinite',
            textAlign: 'center',
          }}
        >
          {label}
        </div>
      )}
      {sublabel && (
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)',
          textAlign: 'center', maxWidth: 280, lineHeight: 1.45,
        }}>
          {sublabel}
        </div>
      )}
    </div>
  );
}
